import { Injectable } from '@angular/core';
import { Observable, throwError, of } from 'rxjs';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import jwt_decode from "jwt-decode";
import { environment } from '../../environments/environment';
import { IAuthToken } from '../interfaces/auth-token';
import { IBrandProfile } from '../interfaces/brand-profile';
import { IDisbursementSession } from '../interfaces/disbursement-session';
import { IDisbursementOccurrence } from '../interfaces/disbursement-occurrence';

const DSB_SESSION_GUID = 'dsb-session-guid';
const ENV_REGION = 'env-region';
const DSB_SESSION = 'dsb-session';
const BRAND_PROFILE = 'brand-profile';
const DISB_OCCURRENCES = 'disb-occurrences';

@Injectable({
  providedIn: 'root'
})
export class SharedService {

  protected walletronRegions = environment.walletronRegions;
  protected disbursementsBasePath = environment.disbursementsBasePath;
  protected walletronBasePath = environment.walletronBasePath;
  protected authServerBaseUrl = environment.authServerBasePth;

  constructor()
  { }

  public jwtDecode(token: string): IAuthToken {
    return jwt_decode(token);
  }  

  public setDsbOccurrences(dsbOccurrences: IDisbursementOccurrence[] | null) {
    localStorage.removeItem(DISB_OCCURRENCES);
    localStorage.setItem(DISB_OCCURRENCES, JSON.stringify(dsbOccurrences));
  }

  public getDsbOccurrences(): IDisbursementOccurrence[] | null {
    let dsbOccurrences = localStorage.getItem(DISB_OCCURRENCES);
    if (dsbOccurrences) {
      return JSON.parse(dsbOccurrences);
    } else {
      return null;
    }
  }


  // Getter and setter for disbursement session guid
  public setDsbSessionGuid(dsbSessionGuid: string){
    localStorage.removeItem(DSB_SESSION_GUID);
    localStorage.setItem(DSB_SESSION_GUID, dsbSessionGuid);
  }

  public getDsbSessionGuid(): any | null{
    return localStorage.getItem(DSB_SESSION_GUID);
  }

  // Getter and setter for enviromental region
  public setEnvRegion(envRegion: string){
    localStorage.removeItem(ENV_REGION);
    localStorage.setItem(ENV_REGION, envRegion);
    // this.envRegion= envRegion;
  }

  public getEnvRegion(): any | null{
    return localStorage.getItem(ENV_REGION);
    // return this.envRegion;
  }

  // Getter and setter for disbursement session
  public setDisbursementSession(disbursementSession: IDisbursementSession){
    localStorage.removeItem(DSB_SESSION);
    localStorage.setItem(DSB_SESSION, JSON.stringify(disbursementSession));
    // this.disbursementSession= disbursementSession;
  }

  public getDisbursementSession(): IDisbursementSession {
    // JSON.parse(localStorage.getItem(DISBURSEMENT_SESSION));
    let dsb_session = localStorage.getItem(DSB_SESSION);
    if (dsb_session) {
      return JSON.parse(dsb_session);
    } else {
      const ds: IDisbursementSession = {sessionId: '', billerId: '', billerUuid: '',  brandCode: '',
       accessToken: '', apiAuthKey: '', billerAccountId: '', fundingAccountTypes: []};
      return ds;
    }
  }

  // Getter and setter for brand profile
  public setBrandProfile(brandProfile: IBrandProfile){
    // console.log('setBrandProfile -  inside', brandProfile);
    localStorage.removeItem(BRAND_PROFILE);
    localStorage.setItem(BRAND_PROFILE, JSON.stringify(brandProfile));
    // this.brandProfile= brandProfile;
  }

  public getBrandProfile(): IBrandProfile{
    let brandProfile = localStorage.getItem(BRAND_PROFILE);
    if (brandProfile) {
      return JSON.parse(brandProfile);
    }
    else {
      const bp: IBrandProfile = {brandCode: '', brandName: ''};
      return bp;
    }
    // return this.brandProfile;
  }

  public getAppBasicAuth(): string {
    return environment.auth;
  }

  public getAuthServerBaseUrl(): string {
    return this.authServerBaseUrl;
  }

  public getDisbursementsBaseUrl(envRegion: string): string {
    if (!envRegion) {
      return '';
    }
    var regionConfig = this.walletronRegions.find(item => item.region === envRegion.toLowerCase());
    if (regionConfig) {
      return regionConfig.baseUrl + this.disbursementsBasePath;
    } else {
      return '';
    }
  }

  public getWalletronBaseUrl(envRegion: string): string {
    var regionConfig = this.walletronRegions.find(item => item.region === envRegion.toLowerCase());
    if (regionConfig) {
      return regionConfig.baseUrl + this.walletronBasePath;
    } else {
      return '';
    }
  }

  public getBrandStyleScheme (brandProfile: IBrandProfile): any {
    let brandStyleTheme = {};
    let configProperties = brandProfile.configProperties;
    for (const propName in configProperties) {
      if (propName == 'DSB_BRAND_STYLE_SCHEME') {
        brandStyleTheme = JSON.parse(configProperties[propName]);
      }
    }
    return brandStyleTheme;
  }

  public getCustomizedPrompt(promptCode: string): string | undefined {
    return this.getConfigProperty('DSB_CUSTOM_PROMPTS', promptCode);
  }

  public getCustomizedTitle(titleCode: string): string | undefined {
    return this.getConfigProperty('DSB_CUSTOM_TITLES', titleCode);
  }

  public getCustomizedLabels(labelCode: string): string | undefined {
    return this.getConfigProperty('DSB_CUSTOM_LABELS', labelCode);
  }

  private getConfigProperty(propertyName: string, variantCode: string): string | undefined{
    let brandProfile = this.getBrandProfile();
    if (brandProfile) {
      let configProperties = brandProfile.configProperties;
      for (const propName in configProperties) {
        if (propName == propertyName) {
          let jsonObject = JSON.parse(configProperties[propName]);
          if (!jsonObject) {
            return '';
          }
          let map = new Map<string, string>();
          for (var value in jsonObject) {
            map.set(value, jsonObject[value]);
          }
          if (map.has(variantCode)) {
            return map.get(variantCode);
          }
        }
      }
    }
    return '';
  }

  public getConfigPropertyByName(propertyName: string): string | undefined{
    let brandProfile = this.getBrandProfile();
    // console.log('getConfigPropertyByName: propertyName', propertyName);
    if (brandProfile) {
      let configProperties = brandProfile.configProperties;
      // console.log('getConfigPropertyByName: configProperties', configProperties);
      for (const propName in configProperties) {
        if (propName == propertyName) {
          // console.log('getConfigPropertyByName: propertyName', configProperties[propName]);
          return configProperties[propName];
        }
      }
    }
    return '';
  }

  public formatString(template: string, ...args: any[]): string {
    return template.replace(/{(\d+)}/g, (match, index) => {
        return typeof args[index] !== 'undefined' ? args[index] : match;
    });
  }
  
  // Common error handling routines
  public handleError(error: HttpErrorResponse) {

    if (error.status === 0) {
      // A client-side or network error occurred. Handle it accordingly.
      console.error('An error occurred:', error.error);
    } else {
      // The backend returned an unsuccessful response code.
      // The response body may contain clues as to what went wrong.
      console.error(
        `Backend returned code ${error.status}, body was: `, error.error);
    }
    // Return an observable with a user-facing error message.
    // return throwError(() => new Error('Something bad happened; please try again later.'));
    return throwError(() => error);
  }

  public handleError2<T>(operation = 'operation', result?: T) {
    return (error: any): Observable<T> => {
      //debugger;

      // TODO: send the error to remote logging infrastructure
      console.error(error); // log to console instead

      // TODO: better job of transforming error for user consumption
      console.log(`${operation} failed: ${error.message}`);

      // Let the app keep running by returning an empty result.
      return of(result as T);
    };
  }

  // public parseQueryString(query: string) {
  //   let vars = query.split("&");
  //   let queryString = new Map<string, string>();
  //   for (let i = 0; i < vars.length; i++) {
  //     let pair: string[] = vars[i].split("=");
  //     let key = decodeURIComponent(pair.shift());
  //     let value = decodeURIComponent(pair.join("="));

  //     // If first entry with this name
  //     if (typeof queryString[key] === "undefined") {
  //       queryString[key] = value;

  //       // If second entry with this name
  //     } else if (typeof query_string[key] === "string") {
  //       var arr = [query_string[key], value];
  //       query_string[key] = arr;
  //       // If third or later entry with this name
  //     } else {
  //       query_string[key].push(value);
  //     }
  //   }
  //   return query_string;
  // }

  // var query_string = "a=1&b=3&c=m2-m3-m4-m5";
  // var parsed_qs = parse_query_string(query_string);
  // console.log(parsed_qs.c);
}
